// Pure helper for the chat panel: turn the plugin's `useCart()` data
// (whose `subtotal` is in EUROS — see src/collections/Cart/index.ts:25 /
// 67) into a CENTS-based snapshot for the chat API (whose
// `formatPriceCents` divides by 100 to render).
//
// UNIT IS CENTS, EVERYWHERE. If you touch this file, also keep
// src/app/(app)/api/chat/route.ts (CartSnapshot type) and
// src/lib/chatHelpers.ts (formatPriceCents) in sync.
//
// Extracted from ChatPanel.tsx so the unit test can pin the conversion
// (regression for the 2026-07-26 bug where €220.00 was sent as 220
// and the bot reported €2.20). See docs/chatbot-sprite/19.

export interface CartSnapshotItemOut {
  productId: string | number
  title: string
  variant?: string | null
  quantity: number
  productSlug?: string | null
  lineTotal: number // CENTS
}

export interface CartSnapshotOut {
  items: CartSnapshotItemOut[]
  subtotal: number // CENTS
  currency: string
}

// Read the per-unit price in euros from the most specific source
// available, falling back to less specific ones. Same precedence as
// src/components/checkout/CheckoutPage.tsx and the cart collection's
// customSubtotalHook. Returns 0 if the product isn't populated (cold
// render, race with the cart fetch) — the snapshot will still go
// through, the bot just won't be able to quote line totals.
function readItemPriceEur(item: any): number {
  const product = item?.product
  const svd = item?.selectedVariantData
  if (svd && typeof svd === 'object' && svd.priceTotal !== undefined) {
    return Number(svd.priceTotal) || 0
  }
  if (item?.variant && typeof item.variant === 'object') {
    const v = item.variant
    return Number(v.priceInEUR) || Number(v.price) || 0
  }
  if (typeof product === 'object' && product !== null) {
    return (
      Number(product.basePrice) ||
      Number(product.priceInEUR) ||
      Number(product.price) ||
      0
    )
  }
  return 0
}

export function buildCartSnapshot(ecommerceCart: any): CartSnapshotOut | null {
  const items = Array.isArray(ecommerceCart?.items) ? ecommerceCart.items : []
  if (items.length === 0) return null

  let subtotalCents = 0
  const snapItems: CartSnapshotItemOut[] = []
  for (const it of items) {
    const product = it?.product
    const id =
      typeof product === 'object' && product !== null ? product.id : product
    if (id == null) continue

    const priceEur = readItemPriceEur(it)
    const quantity = typeof it?.quantity === 'number' ? it.quantity : 1
    // round() handles the float → int step. Product of two ints is
    // exact, but the price coming in can be a float (e.g. 219.95).
    const lineTotalCents = Math.round(priceEur * 100) * quantity
    subtotalCents += lineTotalCents

    snapItems.push({
      productId: id,
      title:
        typeof product === 'object' &&
        product !== null &&
        typeof product.title === 'string'
          ? product.title
          : 'Produkt',
      variant:
        it?.variant && typeof it.variant === 'object' && typeof it.variant.title === 'string'
          ? it.variant.title
          : null,
      quantity,
      productSlug:
        typeof product === 'object' && product !== null && typeof product.slug === 'string'
          ? product.slug
          : null,
      lineTotal: lineTotalCents,
    })
  }
  if (snapItems.length === 0) return null

  return {
    items: snapItems,
    subtotal: subtotalCents, // CENTS
    currency: typeof ecommerceCart?.currency === 'string' ? ecommerceCart.currency : 'EUR',
  }
}
